import React, {
  forwardRef, useEffect, useMemo, useState,
} from 'react';

import { PopoverWithButton } from '@vlabs/monokit/shared/ui/popovers';
import type { PopoverWithButtonProps } from '@vlabs/monokit/shared/ui/popovers';
import { Input } from '@vlabs/uikit/components/controls/input';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

import { useWorkspaceFilters } from '../../useWorkspaceFilters';
import type { VideoAnalyticType } from '../../workspaceFiltersModel';

interface ValidationBoundary {
  message: string;
  value: number;
}

interface ScoreField {
  filterName: string;
  id: string;
  labelKey: string;
  validate?: {
    max?: ValidationBoundary;
    min?: ValidationBoundary;
    validate?: (value: string) => boolean | string;
  };
}

interface VideoAnalyticsScoreFilterPopoverProps extends PopoverWithButtonProps {
  analyticType: VideoAnalyticType;
  scoreField: ScoreField;
}

function getValidationError(value: string, scoreField: ScoreField): string | undefined {
  if (value === '') return undefined;
  const validationResult = scoreField.validate?.validate?.(value);
  if (validationResult !== undefined && validationResult !== true) {
    return String(validationResult);
  }
  const numberValue = Number(value);
  const min = scoreField.validate?.min;
  if (min !== undefined && numberValue < min.value) return String(min.message);
  const max = scoreField.validate?.max;
  if (max !== undefined && numberValue > max.value) return String(max.message);
  return undefined;
}

function VideoAnalyticsScoreFilterPopover(
  {
    analyticType,
    scoreField,
    ...props
  }: VideoAnalyticsScoreFilterPopoverProps,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.JSX.Element {
  const { t } = useTranslation();
  const { filters, updateVideoAnalyticsFilters } = useWorkspaceFilters();
  const analytic = filters.videoAnalytics[analyticType];
  const { filterName } = scoreField;
  const storedValue = String(analytic.filters[filterName] ?? '');
  const [value, setValue] = useState(storedValue);
  const error = getValidationError(value, scoreField);

  useEffect(() => setValue(storedValue), [storedValue]);
  const updateScore = useMemo(() => debounce((nextValue: string | undefined) => {
    updateVideoAnalyticsFilters(analyticType, {
      [filterName]: nextValue,
    });
  }, 500), [analyticType, filterName, updateVideoAnalyticsFilters]);

  useEffect(() => () => updateScore.cancel(), [updateScore]);

  const handleChange = (nextValue: string) => {
    setValue(nextValue);
    if (getValidationError(nextValue, scoreField) !== undefined) return;
    updateScore(nextValue || undefined);
  };

  const reset = () => {
    updateScore.cancel();
    setValue('');
    updateVideoAnalyticsFilters(analyticType, {
      [filterName]: undefined,
    });
  };

  return (
    <PopoverWithButton
      {...props}
      isButtonDisabled={value === ''}
      onClickReset={reset}
      ref={ref}
    >
      <Input
        errorMessage={error}
        hasError={error !== undefined}
        id={scoreField.id}
        isClearable
        label={t(scoreField.labelKey)}
        onChangeValue={handleChange}
        onClear={() => handleChange('')}
        placeholder={t('from')}
        value={value}
      />
    </PopoverWithButton>
  );
}

const VideoAnalyticsScoreFilterPopoverWithRef = forwardRef(
  VideoAnalyticsScoreFilterPopover,
);
export {
  VideoAnalyticsScoreFilterPopoverWithRef as VideoAnalyticsScoreFilterPopover,
};
export type { ScoreField };
